import React, { Fragment, useMemo } from 'react';

import { ChipPanel, ChipPanelProps } from '../../ChipPanel/ChipPanel';

import {
  DropdownCheckboxListOptions,
  DropdownCheckboxListOptionsProps,
  OptionType,
} from './DropdownCheckboxListOptions';

export type { OptionType };

export type DropdownCheckboxListProps = ChipPanelProps &
  DropdownCheckboxListOptionsProps & {
    includeCountInLabel?: boolean;
  };

/**
 * Implements a multi-select dropdown of checkboxes.  The dropdown can be
 * triggered by a chip, a dropdown style box, or a custom element.  On mobile,
 * takes over the screen with an picker.  For use in react-final-form, use
 * a FieldArray from 'react-final-form-arrays' and arrayMutators from 'final-form-arrays';
 *
 * ## Example
 * Renders a checkbox list in the style of DropdownV3 with a form label above
 * the dropdown with a required field label.  In the mobile takeover, uses
 * a custom header and has an "Apply" CTA at the bottom.  The field, selected-events
 * will be an array of strings, with entries in the array indicating the
 * selected options
 *
 * ```typescript
 *
 * import DropdownCheckboxList from '@zola/zola-ui/src/components/Form/DropdownCheckboxList/DropdownCheckboxList';
 *
 * import { FieldArray } from 'react-final-form-arrays';
 * import arrayMutators from 'final-form-arrays';
 *
 * <Form
 *   mutators={{
 *     ...arrayMutators
 *   }}
 *   render={() => {
 *     return (
 *       <form>
 *         <FieldArray name="selected-events">
 *           {({ ...props }) => (
 *             <DropdownCheckboxList
 *               {...props}
 *               options={[ ... ]}
 *               boxTrigger={{ placeholder: "Please make a selection", isPlaceholder: selections.length === 0 }}
 *               formLabel={{ label: 'Which Events would you like photographed', isRequired: true }}
 *               mobileCtaProps={{
 *                 label: 'Apply',
 *               }}
 *               mobileHeader="Which events would you like photographed?"
 *             />
 *           )}
 *         </FieldArray>
 *       </form>
 *     )
 *   }}
 * />
 * ```
 *
 * ## Example
 * Renders a checkbox dropdown in a chip that animates the caret when the
 * dropdown is opened and closed.  The 4th option (white) is disabled.
 *
 * ```typescript
 * const colors:OptionType[] [
 *    { label: 'Red', value: '#F00' },
 *    { label: 'Green', value: '#0F0' },
 *    { label: 'Blue', value: '#00F' },
 *    { label: 'White', value: '#FFF', disabled: true },
 *    { label: 'Black', value: '#000' },
 *  ],
 *
 * <form>
 *   <FieldArray name="colors">
 *     {({ ...props }) => <DropdownCheckboxList options={colors}  withCaret {...props} />}
 *   </FieldArray>
 * </form>
 * ```
 *
 * @see DropdownCheckboxList.stories.tsx
 * @see form.stories
 */
const DropdownCheckboxList = ({
  className,
  options,
  id,
  label,
  formLabel,
  includeCountInLabel,
  onClick,
  mobileCtaProps,
  mobileHeader,
  disabled,
  withCaret,
  customTrigger,
  boxTrigger,
  ...fieldProps
}: DropdownCheckboxListProps): JSX.Element => {
  const { fields } = fieldProps;
  const numSelected = fields?.value?.length || 0;

  // The label that appears in a chip trigger
  const formattedLabel = useMemo(() => {
    const countInLabel = includeCountInLabel && numSelected > 0 ? ` (${numSelected})` : '';
    return (
      <Fragment>
        {label}
        {countInLabel}
      </Fragment>
    );
  }, [includeCountInLabel, label, numSelected]);

  return (
    <ChipPanel
      className={className}
      id={id}
      label={formattedLabel}
      formLabel={formLabel}
      onClick={onClick}
      mobileCtaProps={mobileCtaProps}
      mobileHeader={mobileHeader}
      withCaret={withCaret}
      disabled={disabled || (options || []).length < 1}
      boxTrigger={boxTrigger}
      customTrigger={customTrigger}
    >
      <DropdownCheckboxListOptions id={id} options={options} {...fieldProps} />
    </ChipPanel>
  );
};

export default DropdownCheckboxList;
